/**
 * Reusable Skeleton SVG Component
 * Provides animated gradient skeleton loading states
 */

import React, { SVGAttributes } from 'react';
import { useTheme } from 'styled-components';

import { IOcAppTheme } from '../OcWidget/config/ocTheme';
import { StyledSkeletonSVG } from './SkeletonSVG.styled';

/**
 * OcSkeletonDefs Component
 * Defines the animated gradient used by all skeleton elements
 * Must be rendered once at the top level of the app
 */
export const OcSkeletonDefs: React.FC = () => {
  const theme = useTheme() as IOcAppTheme;
  
  const lightColor = theme.color.loadingBackgroundGradientLight || 'rgba(240, 240, 240, 0.25)';
  const darkColor = theme.color.loadingBackgroundGradientDark || 'rgba(100, 100, 100, 0.2)';
  
  return (
    <svg aria-hidden style={{ height: 0, width: 0, position: 'absolute' }}>
      <defs>
        <linearGradient id="skeleton-svg-gradient" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor={lightColor}>
            <animate
              attributeName="stop-color"
              values={`${lightColor}; ${darkColor}; ${lightColor};`}
              dur="3s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="100%" stopColor={darkColor}>
            <animate
              attributeName="stop-color"
              values={`${darkColor}; ${lightColor}; ${darkColor};`}
              dur="3s"
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
      </defs>
    </svg>
  );
};

/**
 * SkeletonSVG Component
 * Wrapper for SVG skeleton elements that automatically applies the gradient
 * All rect and circle elements inside will use the animated gradient fill
 */
const SkeletonSVG: React.FC<SVGAttributes<SVGElement>> = ({ 
  children, 
  className = '', 
  ...svgAttrs 
}) => (
  <StyledSkeletonSVG 
    preserveAspectRatio="none" 
    className={className} 
    {...svgAttrs}
  >
    {children}
  </StyledSkeletonSVG>
);

export default React.memo(SkeletonSVG);

